import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaHistory } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { discoveryService } from '../../services/apiService';
import { useVehicle } from '../../contexts/VehicleContext';

export default function VehiclePartsBrowse() {
  const navigate = useNavigate();
  const { selectedVehicle } = useVehicle();
  const vehicleId = selectedVehicle?._id || selectedVehicle?.id || '';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!vehicleId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await discoveryService.browseVehicle(vehicleId);
      setData(res?.data || res);
    } catch (err) {
      toast.error(err.message || 'Could not load vehicle parts');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!vehicleId) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-slate-600 mb-4">Save a vehicle to browse parts for your car.</p>
        <Link to="/account/vehicles" className="text-indigo-600 font-semibold hover:underline">
          Add vehicle
        </Link>
      </div>
    );
  }

  const ordered = data?.previouslyOrderedParts || [];
  const subSystems = data?.subSystems || [];

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <FaArrowLeft /> Back
        </button>

        <h1 className="text-2xl font-bold text-slate-900 mb-1">Parts for your vehicle</h1>
        {data?.vehicle && (
          <p className="text-sm text-slate-600 mb-6">
            {[data.vehicle.make, data.vehicle.model, data.vehicle.year, data.vehicle.variant]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}

        {ordered.length > 0 && (
          <section className="mb-8">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-2">
              <FaHistory /> Ordered before
            </h2>
            <ul className="space-y-2">
              {ordered.map((part) => (
                <li key={part.catalogNodeId}>
                  <Link
                    to={`/catalog/node/${part.catalogNodeId}?vehicleId=${vehicleId}`}
                    className="block rounded-xl border border-indigo-200 bg-indigo-50/50 px-4 py-3 hover:bg-indigo-50"
                  >
                    <p className="font-medium text-slate-900">{part.partCategory}</p>
                    <p className="text-xs text-slate-500">{part.subSystem}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
              Sub-systems ({subSystems.filter((s) => (s.partCount ?? 0) > 0).length})
            </h2>
            <Link
              to={`/discovery/subsystems?vehicleId=${vehicleId}`}
              className="text-xs font-semibold text-indigo-600 hover:underline"
            >
              View all
            </Link>
          </div>

          {loading ? (
            <p className="text-slate-500 text-center py-8">Loading…</p>
          ) : (
            <ul className="grid grid-cols-2 gap-2">
              {subSystems
                .filter((s) => (s.partCount ?? s.partCategoryCount ?? 0) > 0)
                .map((sub) => {
                  const name = sub.name || sub.subSystem;
                  return (
                    <li key={name}>
                      <Link
                        to={`/discovery/subsystem/${encodeURIComponent(name)}?vehicleId=${vehicleId}`}
                        className="block rounded-lg border border-slate-200 bg-white p-3 text-sm font-medium text-slate-800 hover:border-indigo-300"
                      >
                        {name}
                        <span className="block text-xs text-slate-400 font-normal mt-0.5">
                          {sub.partCount ?? sub.partCategoryCount} parts
                        </span>
                      </Link>
                    </li>
                  );
                })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
